/**
 * Platform-agnostic Sentry error-filtering logic.
 *
 * Shared by the native (`@ergon-ai/utils-native`) and web (`@ergon-ai/utils-web`)
 * loggers and Sentry `beforeSend` hooks. Each platform supplies its own
 * `FILTERED_ERROR_NAMES` / `FILTERED_ERROR_PATTERNS` (which intentionally
 * differ — e.g. native also filters the cold-start malformed-auth-token and the
 * RevenueCat purchase restriction) and binds them with `createErrorFilter()`.
 * The decision logic, the crash safeguard, and the DOMException-safe
 * name/message reader live here once, so they can only be fixed (or regressed)
 * in a single place.
 *
 * These patterns identify user-facing errors that should NOT pollute Sentry
 * Issues — expected conditions (validation, network, auth lifecycle, request
 * cancellation), not application bugs.
 */

/**
 * Error class names shared by every platform: user-facing validation errors and
 * request cancellations (AbortError). Platforms extend this with their own names
 * if needed.
 */
export const BASE_FILTERED_ERROR_NAMES = [
  "zoderror",
  "validationerror",
  // GraphQL user-input rejections (the spec's BAD_USER_INPUT code, e.g. a bad
  // phone/email/enum caught by schema directives). The graphql-api hooks put the
  // backend's `extensions.code` on the thrown error's name, so this matches the
  // real wire classification. The "inputfieldproblem" message patterns below
  // never could: they check the human message text, which carries the field
  // message ("Phone number is invalid"), never the type name.
  "bad_user_input",
  // Request cancellations (route change / navigation away mid-fetch, timeout
  // aborts). An aborted operation is never an actionable crash.
  "aborterror",
] as const;

/**
 * Message patterns shared by every platform. All patterns are lowercase for
 * case-insensitive matching.
 *
 * Categories:
 * - Validation: User input issues (form validation, duplicate entries)
 * - Network: User's connection issues (not our server's fault)
 * - Auth: Normal session lifecycle (token expiry is expected)
 * - Permission: User denied access (expected behavior)
 */
export const BASE_FILTERED_ERROR_PATTERNS = [
  // Validation errors (user input issues)
  "validation error",
  "validation errors",
  "inputfieldproblem",
  "multipleinputfieldsproblem",
  "invalid login credentials",
  "duplicate",
  "already exists",
  "invalid email",
  "invalid format",
  "required field",
  "password too short",
  "passwords do not match",
  "password must",
  "email must",
  "name must",
  "phone must",

  // Network errors (user's connection issues)
  "network request failed",
  "network request timed out",

  // Auth errors (normal session lifecycle)
  "token expired",
  "session expired",
  "refresh token",
  "unauthorized",
  "not authenticated",
  "jwt expired",

  // Permission errors (user denied access)
  "permission denied",
  "permission not granted",

  // Android intent errors (user's device configuration)
  "activitynotfoundexception",
  "no activity found to handle intent",
] as const;

/**
 * Whether a Sentry event represents a crash that must ALWAYS be sent,
 * regardless of any message/name filtering.
 *
 * Crash-free session/user rate is the north-star reliability metric, so an
 * unhandled exception or a fatal-level event is never silenced — even if its
 * message happens to match a noise pattern. The rule: an event is a crash
 * (always sent) only when `handled === false` OR `level === "fatal"`; every
 * other event — handled, or unknown-handled (`undefined`), and non-fatal — is
 * eligible for filtering.
 *
 * @param level - Sentry event level (e.g. "error", "fatal")
 * @param handled - `mechanism.handled` from the event's exception (undefined when unknown)
 */
export const isCrashLevelEvent = (
  level: string | undefined,
  handled: boolean | undefined,
): boolean => handled === false || level === "fatal";

/**
 * Reads an error's `name`/`message` WITHOUT narrowing to `instanceof Error`.
 *
 * AbortError arrives as a DOMException (and whatwg-fetch's polyfill does NOT
 * extend Error), so an `instanceof Error` check would lose its real
 * "AbortError" name and message — both for filtering (isUserFacingError) and
 * for the human-readable `[Filtered]` breadcrumb. This is the single duck-typed
 * reader both paths share. Non-string message → "" rather than `String(error)`,
 * which would just yield an unhelpful "[object Object]"; safe for any non-Error
 * value, so `beforeSend` never throws.
 */
export const getErrorNameAndMessage = (
  error: unknown,
): { name: string; message: string } => {
  if (typeof error === "string") return { name: "Error", message: error };
  if (error !== null && typeof error === "object") {
    const e = error as { name?: unknown; message?: unknown };
    return {
      name: typeof e.name === "string" ? e.name : "Error",
      message: typeof e.message === "string" ? e.message : "",
    };
  }
  return { name: "Error", message: "" };
};

/**
 * Error categories for Sentry tagging and monitoring.
 */
export type ErrorCategory =
  "validation" | "network" | "permission" | "auth" | "application";

/**
 * Categorizes an error for Sentry tags based on its characteristics.
 */
export const categorizeError = (error: Error): ErrorCategory => {
  const name = error.name.toLowerCase();
  const message = error.message.toLowerCase();

  // Validation error classes. Note: AbortError is filtered noise too, but it is
  // a request cancellation (network), handled below — not a validation error.
  if (name === "zoderror" || name === "validationerror") {
    return "validation";
  }

  // Request cancellations (AbortError) are network-related, not validation.
  if (name === "aborterror") {
    return "network";
  }

  // Check by message patterns
  if (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("connection")
  ) {
    return "network";
  }

  if (message.includes("permission") || message.includes("denied")) {
    return "permission";
  }

  if (
    message.includes("auth") ||
    message.includes("login") ||
    message.includes("token") ||
    message.includes("session")
  ) {
    return "auth";
  }

  return "application";
};

/**
 * Rate limiting configuration for breadcrumbs.
 * Prevents flooding Sentry with repeated filtered errors.
 */
export const BREADCRUMB_RATE_LIMIT = {
  /** Maximum breadcrumbs per time window */
  maxCount: 10,
  /** Time window in milliseconds (1 minute) */
  windowMs: 60_000,
} as const;

/** The platform-specific name/pattern lists that bind an {@link ErrorFilter}. */
export interface ErrorFilterConfig {
  /** Lowercase error class names treated as user-facing noise. */
  names: readonly string[];
  /** Lowercase message substrings treated as user-facing noise. */
  patterns: readonly string[];
}

/** The bound matchers + suppression decision returned by {@link createErrorFilter}. */
export interface ErrorFilter {
  isFilteredErrorName: (name: string) => boolean;
  isFilteredErrorMessage: (message: string) => boolean;
  isUserFacingError: (error: unknown) => boolean;
  shouldSuppressEvent: (
    level: string | undefined,
    handled: boolean | undefined,
    error: unknown,
  ) => boolean;
}

/**
 * Binds the shared decision logic to a platform's name/pattern lists.
 *
 * - `isFilteredErrorName` / `isFilteredErrorMessage`: case-insensitive matchers.
 * - `isUserFacingError`: expected, user-facing condition (validation, network,
 *   auth lifecycle, request cancellation). Handles Error instances, string
 *   captures (captureMessage) and DOMException-shaped errors via duck-typing.
 * - `shouldSuppressEvent`: drop an event only when it is user-facing AND not a
 *   crash. Crashes (unhandled/fatal) are always sent.
 */
export const createErrorFilter = ({
  names,
  patterns,
}: ErrorFilterConfig): ErrorFilter => {
  const isFilteredErrorName = (name: string): boolean => {
    const lowerName = name.toLowerCase();
    return names.some((pattern) => lowerName === pattern);
  };

  const isFilteredErrorMessage = (message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    return patterns.some((pattern) => lowerMessage.includes(pattern));
  };

  const isUserFacingError = (error: unknown): boolean => {
    if (!error) return false;
    const { name, message } = getErrorNameAndMessage(error);
    return isFilteredErrorName(name) || isFilteredErrorMessage(message);
  };

  const shouldSuppressEvent = (
    level: string | undefined,
    handled: boolean | undefined,
    error: unknown,
  ): boolean => !isCrashLevelEvent(level, handled) && isUserFacingError(error);

  return {
    isFilteredErrorName,
    isFilteredErrorMessage,
    isUserFacingError,
    shouldSuppressEvent,
  };
};
